/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useState,
  useMemo,
  createContext,
  useContext,
  ReactNode,
} from "react";

export interface SelectProps {
  label: string;
  value: string;
}

export interface DataProps {
  IdsPdvs: SelectProps[] | null;
  IdsSuperintendentes: SelectProps[] | null;
  IdsEquipes: SelectProps[] | null;
  DataInicio: string | null;
  DataFim: string | null;
  IdsCorretores: SelectProps[] | null;
}

export interface FilterCheckinContextValue {
  filtros: DataProps | null;
  atualizarFilterCheckin: (obj: DataProps | null) => void;
  limparFilterCheckin: () => void;
  queryParams: string;
  activeFilterCount: number;
  atualizarCamposDesabilitados: (
    campo: "superintendente" | "equipe",
    valor: boolean
  ) => void;
  disabledFields: {
    superintendente: boolean;
    equipe: boolean;
  };
}

const FilterCheckinContext = createContext<
  FilterCheckinContextValue | undefined
>(undefined);

interface FilterCheckinProviderProps {
  children: ReactNode;
}

const siblingGroups: (keyof DataProps)[][] = [
  ["DataInicio", "DataFim"], // Grupo de irmãos
];

const countActiveFiltersWithSiblings = (
  filters: DataProps | null,
  siblingGroups: (keyof DataProps)[][],
  disabledFields: {
    superintendente: boolean;
    equipe: boolean;
  }
): number => {
  if (!filters) return 0;

  let count = 0;
  const handledSiblings = new Set<keyof DataProps>();

  siblingGroups.forEach((group) => {
    const hasActiveSibling = group.some(
      (key) => !handledSiblings.has(key) && filters[key]
    );
    if (hasActiveSibling) {
      count += 1;
      group.forEach((key) => handledSiblings.add(key));
    }
  });

  Object.keys(filters).forEach((key) => {
    const typedKey = key as keyof DataProps;

    // Pular campos desabilitados
    if (typedKey === "IdsSuperintendentes" && disabledFields.superintendente) {
      return;
    }
    if (typedKey === "IdsEquipes" && disabledFields.equipe) {
      return;
    }

    if (!handledSiblings.has(typedKey)) {
      if (Array.isArray(filters[typedKey])) {
        if ((filters[typedKey] as SelectProps[])?.length > 0) {
          count += 1;
        }
      } else if (filters[typedKey]) {
        count += 1;
      }
    }
  });

  return count;
};

export const FilterCheckinProvider: React.FC<FilterCheckinProviderProps> = ({
  children,
}) => {
  const [filtros, setFiltros] = useState<DataProps | null>({
    IdsPdvs: [],
    IdsSuperintendentes: [],
    IdsEquipes: [],
    DataInicio: null,
    DataFim: null,
    IdsCorretores: [],
  });

  // Adicionando estados para controlar campos desabilitados
  const [disabledFields, setDisabledFields] = useState<{
    superintendente: boolean;
    equipe: boolean;
  }>({
    superintendente: false,
    equipe: false,
  });

  const activeFilterCount = useMemo(
    () =>
      countActiveFiltersWithSiblings(filtros, siblingGroups, disabledFields),
    [filtros, siblingGroups, disabledFields]
  );

  const generateQueryParams = (data: Record<string, any> | null): string => {
    if (!data) return "";

    const params = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
      // Adequação para camelCase na URL
      let paramKey = key;
      if (key === "IdsPdvs") paramKey = "IdsPdvs";
      if (key === "IdsSuperintendentes") paramKey = "IdsSuperintendentes";
      if (key === "IdsEquipes") paramKey = "IdsEquipes";
      if (key === "DataInicio") paramKey = "DataInicio";
      if (key === "DataFim") paramKey = "DataFim";
      if (key === "IdsCorretores") paramKey = "IdsCorretores";

      if (Array.isArray(value)) {
        // Trata arrays (por exemplo, `IdsPdvs`, `IdsCorretores`, etc.)
        value.forEach((item) => {
          if (typeof item === "object" && item.value) {
            params.append(paramKey, item.value);
          } else if (typeof item === "string") {
            params.append(paramKey, item);
          }
        });
      } else if (typeof value === "string") {
        // Trata strings simples, como `DataInicio` e `DataFim`
        params.append(paramKey, value);
      } else if (typeof value === "object" && value !== null) {
        // Trata objetos com propriedade `value`
        if (value.value) {
          params.append(paramKey, value.value);
        }
      }
    });

    const queryString = params.toString();
    return queryString ? `&${queryString}` : "";
  };

  const queryParams = useMemo(() => generateQueryParams(filtros), [filtros]);

  const atualizarFilterCheckin = (obj: DataProps | null) => {
    setFiltros(obj);
  };

  const limparFilterCheckin = () => {
    if (filtros) {
      const novosFiltros: DataProps = {
        IdsPdvs: [],
        // Preservar IdsSuperintendentes se estiver desabilitado
        IdsSuperintendentes: disabledFields.superintendente
          ? filtros.IdsSuperintendentes
          : [],
        // Preservar IdsEquipes se estiver desabilitado
        IdsEquipes: disabledFields.equipe ? filtros.IdsEquipes : [],
        DataInicio: null,
        DataFim: null,
        IdsCorretores: [],
      };
      setFiltros(novosFiltros);
    }
  };

  // Função para atualizar campos desabilitados
  const atualizarCamposDesabilitados = (
    campo: "superintendente" | "equipe",
    valor: boolean
  ) => {
    setDisabledFields((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const contextValue = useMemo(
    () => ({
      filtros,
      atualizarFilterCheckin,
      limparFilterCheckin,
      queryParams,
      activeFilterCount,
      atualizarCamposDesabilitados,
      disabledFields,
    }),
    [filtros, queryParams, activeFilterCount, disabledFields]
  );

  return (
    <FilterCheckinContext.Provider value={contextValue}>
      {children}
    </FilterCheckinContext.Provider>
  );
};

export const useFilterCheckin = (): FilterCheckinContextValue => {
  const context = useContext(FilterCheckinContext);
  if (!context) {
    throw new Error(
      "useFilterCheckin deve ser usado dentro de um FilterCheckinProvider"
    );
  }
  return context;
};
